import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  cancelDaemonTrigger,
  continueDaemonTrigger,
  createDaemonTrigger,
  deleteDaemonTrigger,
  fetchDaemonTriggerById,
  fetchDaemonTriggerInjectedConventions,
  fetchDaemonTriggerLogs,
  fetchDaemonTriggers,
  requestWorktreeRemoval,
  resolveDaemonTriggerSettings,
  setDaemonTriggerFavorite,
  type ContinueDaemonTriggerInput,
  type DaemonTriggerInjectedConvention,
  type DaemonTriggerLog,
  type DaemonTrigger,
  type CreateDaemonTriggerInput,
  type ResolveDaemonTriggerSettingsInput,
  type PagedData,
} from '../../lib/api';
import { queryKeys } from './queryKeys';

const getDaemonTriggerChainKey = (trigger: DaemonTrigger) => trigger.chainId ?? trigger.id;

const invalidateTriggerNavigationBadges = (
  queryClient: QueryClient,
  projectId?: string | null,
  triggerId?: string | null,
) => {
  const cachedProjectId = triggerId
    ? queryClient.getQueryData<DaemonTrigger>(queryKeys.daemonTriggers.detail(triggerId))?.projectId
    : undefined;
  const id = projectId ?? cachedProjectId;
  void queryClient.invalidateQueries({
    queryKey: id ? queryKeys.navigationBadges.project(id) : queryKeys.navigationBadges.all,
  });
};

const invalidateHomeRecentRuns = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({ queryKey: queryKeys.me.recentRuns });
  void queryClient.invalidateQueries({ queryKey: queryKeys.me.recentRunsByGroup('active') });
  void queryClient.invalidateQueries({ queryKey: queryKeys.me.recentRunsByGroup('past') });
  void queryClient.invalidateQueries({ queryKey: queryKeys.me.recentRunsByGroup('all') });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.me.recentRunsByGroup('active', undefined, { isFavorite: true }),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.me.recentRunsByGroup('past', undefined, { isFavorite: true }),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.me.recentRunsByGroup('all', undefined, { isFavorite: true }),
  });
};

const setFavoriteForChain = (triggers: DaemonTrigger[], triggerId: string, isFavorite: boolean) => {
  const targetTrigger = triggers.find((trigger) => trigger.id === triggerId);
  if (!targetTrigger) {
    return triggers;
  }

  const targetChainKey = getDaemonTriggerChainKey(targetTrigger);

  return triggers.map((trigger) =>
    getDaemonTriggerChainKey(trigger) === targetChainKey ? { ...trigger, isFavorite } : trigger,
  );
};

type DaemonTriggerListCache = { data: DaemonTrigger[]; meta: unknown };
type DaemonTriggerInfiniteCache = {
  pages: Array<{ data: DaemonTrigger[]; meta: unknown }>;
  pageParams: unknown[];
};

const getFavoriteTargetChainKey = (
  triggerId: string,
  detail: DaemonTrigger | undefined,
  listQueries: Array<[unknown, DaemonTriggerListCache | undefined]>,
  infiniteQueries: Array<[unknown, DaemonTriggerInfiniteCache | undefined]>,
) => {
  if (detail) return getDaemonTriggerChainKey(detail);

  for (const [, query] of listQueries) {
    const target = query?.data.find((trigger) => trigger.id === triggerId);
    if (target) return getDaemonTriggerChainKey(target);
  }

  for (const [, query] of infiniteQueries) {
    for (const page of query?.pages ?? []) {
      const target = page.data.find((trigger) => trigger.id === triggerId);
      if (target) return getDaemonTriggerChainKey(target);
    }
  }

  return triggerId;
};

const shouldSetFavoriteForDetail = (trigger: DaemonTrigger, triggerId: string, targetChainKey: string) =>
  trigger.id === triggerId || getDaemonTriggerChainKey(trigger) === targetChainKey;

export const useDaemonTriggers = (
  token: string | null,
  params?: {
    status?: DaemonTrigger['status'];
    projectId?: string;
    agentConfigId?: string;
    targetDaemonId?: string;
    runnerType?: string;
    prompt?: string;
    limit?: number;
  },
) => {
  return useQuery({
    queryKey: queryKeys.daemonTriggers.list(params ?? {}),
    queryFn: () => fetchDaemonTriggers(token as string, params),
    enabled: Boolean(token),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 30_000,
    select: (result) => result.data,
  });
};

export const useDaemonTriggerById = (token: string | null, triggerId: string | null) => {
  return useQuery({
    queryKey: queryKeys.daemonTriggers.detail(triggerId ?? ''),
    queryFn: () => fetchDaemonTriggerById(token as string, triggerId as string),
    enabled: Boolean(token) && Boolean(triggerId),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 30_000,
  });
};

export const useDaemonTriggerResolvedSettings = (
  token: string | null,
  input: ResolveDaemonTriggerSettingsInput | null,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.daemonTriggers.resolvedSettings(input ?? {}),
    queryFn: () => resolveDaemonTriggerSettings(token as string, input as ResolveDaemonTriggerSettingsInput),
    enabled: Boolean(token) && Boolean(input?.agentConfigId) && (options?.enabled ?? true),
    staleTime: 15_000,
  });
};

export const useDaemonTriggersInfinite = (
  token: string | null,
  params?: {
    status?: DaemonTrigger['status'];
    projectId?: string;
    agentConfigId?: string;
    agentConfigIds?: string[];
    targetDaemonId?: string;
    runnerType?: string;
    prompt?: string;
    planMode?: boolean;
    useWorktree?: boolean;
    isFavorite?: boolean;
    groupByChain?: boolean;
    dateFrom?: string;
    dateTo?: string;
  },
  options?: { enabled?: boolean },
) => {
  return useInfiniteQuery({
    queryKey: queryKeys.daemonTriggers.infinite(params ?? {}),
    queryFn: ({ pageParam }) =>
      fetchDaemonTriggers(token as string, { ...params, page: pageParam as number, pageSize: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
    enabled: Boolean(token) && (options?.enabled ?? true),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 30_000,
    select: (data) =>
      ({
        pages: data.pages,
        pageParams: data.pageParams,
        data: data.pages.flatMap((p) => p.data),
        meta: data.pages[data.pages.length - 1]?.meta,
      }) as unknown as PagedData<DaemonTrigger>,
  });
};

export const usePlanActiveDaemonTriggers = (
  token: string | null,
  projectId: string | undefined,
  planId: string | undefined,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.daemonTriggers.list({ status: 'active', projectId, planId, limit: 20 }),
    queryFn: () =>
      fetchDaemonTriggers(token as string, {
        status: 'active' as DaemonTrigger['status'],
        projectId,
        planId,
        limit: 20,
      }),
    enabled: Boolean(token) && Boolean(projectId) && Boolean(planId) && (options?.enabled ?? true),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 30_000,
    select: (result) => result.data,
  });
};

export const useActiveDaemonTriggers = (token: string | null, projectId?: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.daemonTriggers.list({ status: 'active', projectId, limit: 100 }),
    queryFn: () =>
      fetchDaemonTriggers(token as string, { status: 'active' as DaemonTrigger['status'], projectId, limit: 100 }),
    enabled: Boolean(token) && (options?.enabled ?? true),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 30_000,
    select: (result) => result.data,
  });
};

export const useActiveDaemonTriggerCount = (token: string | null, projectId?: string) => {
  return useQuery({
    queryKey: queryKeys.daemonTriggers.list({ status: 'active', projectId, limit: 100 }),
    queryFn: () =>
      fetchDaemonTriggers(token as string, { status: 'active' as DaemonTrigger['status'], projectId, limit: 100 }),
    enabled: Boolean(token) && Boolean(projectId),
    staleTime: 30_000,
    select: (result) => result.data,
  });
};

export const useCreateDaemonTriggerMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateDaemonTriggerInput) => createDaemonTrigger(token as string, input),
    onSettled: (data, _error, variables) => {
      invalidateTriggerNavigationBadges(queryClient, data?.projectId ?? variables.projectId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.daemonTriggers.all });
      invalidateHomeRecentRuns(queryClient);
    },
  });
};

export const useDeleteDaemonTriggerMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDaemonTrigger(token as string, id),
    onSettled: (_data, _error, id) => {
      invalidateTriggerNavigationBadges(queryClient, undefined, id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.daemonTriggers.all });
      invalidateHomeRecentRuns(queryClient);
    },
  });
};

export const useContinueDaemonTriggerMutation = (token: string | null, triggerId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ContinueDaemonTriggerInput) =>
      continueDaemonTrigger(token as string, triggerId as string, input),
    onSettled: (data) => {
      invalidateTriggerNavigationBadges(queryClient, data?.projectId, triggerId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.daemonTriggers.all });
      invalidateHomeRecentRuns(queryClient);
    },
  });
};

export const useCancelDaemonTriggerMutation = (token: string | null, triggerId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => cancelDaemonTrigger(token as string, triggerId as string),
    onSettled: (data) => {
      invalidateTriggerNavigationBadges(queryClient, data?.projectId, triggerId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.daemonTriggers.all });
      invalidateHomeRecentRuns(queryClient);
    },
  });
};

export const useRequestWorktreeRemovalMutation = (token: string | null, triggerId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => requestWorktreeRemoval(token as string, triggerId as string),
    onSettled: () => {
      invalidateTriggerNavigationBadges(queryClient, undefined, triggerId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.daemonTriggers.all });
      invalidateHomeRecentRuns(queryClient);
    },
  });
};

const LOG_STREAM_PAGE_SIZE = 200;
const LOG_STREAM_POLL_INTERVAL_MS = 2_000;
// 한 폴링 사이클에서 비울 최대 페이지 수(폭주/이상 cursor 대비 안전 가드).
const LOG_STREAM_MAX_DRAIN_PAGES = 50;

/**
 * 러너 트리거 로그를 cursor 기반 증분 스트리밍으로 누적한다.
 *
 * - 최초 마운트 시 전체 로그를 페이지네이션으로 모두 끌어와 표시한다.
 * - `active`(진행 중)일 때만 일정 간격으로 폴링하며, 매 폴링은 마지막 cursor
 *   이후 새로 생긴 줄만 받아 기존 목록 뒤에 append 한다(전량 재조회 X).
 * - 종료(terminal) 상태로 전환되면 폴링을 멈추되, 마지막 줄을 놓치지 않도록
 *   전환 직후 한 번 더 비운다.
 *
 * `active` 판정(종료 상태 집합)은 호출 측에서 trigger.status 기준으로 넘긴다.
 */
export const useDaemonTriggerLogStream = (
  token: string | null,
  triggerId: string | null,
  options?: { active?: boolean },
) => {
  const active = options?.active ?? false;

  const [logs, setLogs] = useState<DaemonTriggerLog[]>([]);
  const cursorRef = useRef<string | null>(null);
  const seenIdsRef = useRef<Set<string>>(new Set());

  // 대상 트리거가 바뀌면 누적 상태를 초기화한다(이전 트리거 로그 혼입 방지).
  useEffect(() => {
    cursorRef.current = null;
    seenIdsRef.current = new Set();
    setLogs([]);
  }, [triggerId]);

  const query = useQuery({
    queryKey: queryKeys.daemonTriggers.logStream(triggerId ?? ''),
    enabled: Boolean(token) && Boolean(triggerId),
    // 진행 중인 트리거만 폴링한다. 종료 상태면 최초 1회만 조회한다.
    refetchInterval: active ? LOG_STREAM_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
    // 폴링이 직접 새 줄을 append 하므로 캐시 staleness 의미가 없다.
    staleTime: 0,
    queryFn: async () => {
      const requestedTriggerId = triggerId;
      const appended: DaemonTriggerLog[] = [];
      let cursor = cursorRef.current ?? undefined;

      // 마지막 cursor 이후의 모든 페이지를 비운다 — 직전 폴링 이후 페이지 크기보다
      // 많은 로그가 쌓였어도 한 사이클에 전부 수집한다.
      for (let page = 0; page < LOG_STREAM_MAX_DRAIN_PAGES; page += 1) {
        const result = await fetchDaemonTriggerLogs(token as string, requestedTriggerId as string, {
          cursor,
          limit: LOG_STREAM_PAGE_SIZE,
        });

        for (const log of result.logs) {
          if (!seenIdsRef.current.has(log.id)) {
            seenIdsRef.current.add(log.id);
            appended.push(log);
          }
        }

        const lastLog = result.logs[result.logs.length - 1];
        if (lastLog) {
          cursor = lastLog.id;
        }

        if (!result.nextCursor) {
          break;
        }
      }

      // 응답이 도착하기 전에 트리거가 바뀌었으면(스테일) 누적에 반영하지 않는다.
      if (requestedTriggerId === triggerId && appended.length > 0) {
        cursorRef.current = cursor ?? null;
        setLogs((prev) => prev.concat(appended));
      }

      return appended.length;
    },
  });

  // 진행 중 → 종료 전환 직후, 폴링이 멈추기 전에 마지막 줄을 한 번 더 비운다.
  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;
  const wasActiveRef = useRef(active);
  useEffect(() => {
    if (wasActiveRef.current && !active) {
      void refetchRef.current();
    }
    wasActiveRef.current = active;
  }, [active]);

  return {
    logs,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
  };
};

export const useDaemonTriggerInjectedConventions = (token: string | null, triggerId: string | null) => {
  return useQuery<DaemonTriggerInjectedConvention[]>({
    queryKey: queryKeys.daemonTriggers.injectedConventions(triggerId ?? ''),
    queryFn: () => fetchDaemonTriggerInjectedConventions(token as string, triggerId as string),
    enabled: Boolean(token) && Boolean(triggerId),
    staleTime: 60_000,
  });
};

export const useToggleDaemonTriggerFavoriteMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ triggerId, isFavorite }: { triggerId: string; isFavorite: boolean }) =>
      setDaemonTriggerFavorite(token as string, triggerId, isFavorite),
    onMutate: async ({ triggerId, isFavorite }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.daemonTriggers.all });

      const previousListQueries = queryClient.getQueriesData<DaemonTriggerListCache>({
        queryKey: queryKeys.daemonTriggers.lists,
      });
      const previousInfiniteQueries = queryClient.getQueriesData<DaemonTriggerInfiniteCache>({
        queryKey: queryKeys.daemonTriggers.infinites,
      });
      const previousDetail = queryClient.getQueryData<DaemonTrigger>(queryKeys.daemonTriggers.detail(triggerId));
      const previousDetails = queryClient.getQueriesData<DaemonTrigger>({
        queryKey: queryKeys.daemonTriggers.details,
      });
      const targetChainKey = getFavoriteTargetChainKey(
        triggerId,
        previousDetail,
        previousListQueries,
        previousInfiniteQueries,
      );

      queryClient.setQueriesData<DaemonTriggerListCache>({ queryKey: queryKeys.daemonTriggers.lists }, (old) =>
        old
          ? {
              ...old,
              data: setFavoriteForChain(old.data, triggerId, isFavorite),
            }
          : old,
      );

      queryClient.setQueriesData<DaemonTriggerInfiniteCache>({ queryKey: queryKeys.daemonTriggers.infinites }, (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                data: setFavoriteForChain(page.data, triggerId, isFavorite),
              })),
            }
          : old,
      );

      queryClient.setQueriesData<DaemonTrigger>({ queryKey: queryKeys.daemonTriggers.details }, (old) =>
        old && shouldSetFavoriteForDetail(old, triggerId, targetChainKey) ? { ...old, isFavorite } : old,
      );

      return { previousQueries: [...previousListQueries, ...previousInfiniteQueries], previousDetails };
    },
    onError: (_error, _triggerId, context) => {
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
      if (context?.previousDetails) {
        for (const [key, data] of context.previousDetails) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.daemonTriggers.all });
      invalidateHomeRecentRuns(queryClient);
    },
  });
};
